import React, { useState, useEffect } from "react";
import moment from "moment";
import {
  Box,
  Container,
  IconButton,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Button,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMoneyBillTransfer } from "@fortawesome/free-solid-svg-icons";

import { getToken } from "../../../api/Account";
import { getListTipoCambioAsync } from "../../../api/TipoCambio";
import { formatearFecha, formatearMonto } from "../../../helpers/formato";
import {
  CargandoTabla,
  EstadoError,
  EstadoVacio,
  KpiCard,
  PageHeader,
} from "../../../components/base";
import { PAGINACION_ARIA, encabezadoTabla, useCargaLocal } from "../comunes";

const FILAS_POR_PAGINA = 15;

/**
 * Historial del tipo de cambio oficial (córdobas por dólar), de solo lectura: lo actualiza el
 * sistema. Del más reciente al más antiguo, con búsqueda por fecha en el formato de la app.
 */
const TasaCambioList = () => {
  const token = getToken();
  const [lista, setLista] = useState([]);
  const [recargar, setRecargar] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [pagina, setPagina] = useState(0);

  const { cargando, error, cargar } = useCargaLocal(() => getListTipoCambioAsync(token));

  useEffect(() => {
    let vigente = true;
    (async () => {
      const result = await cargar();
      // Este endpoint entrega la lista en `data` (no en `data.result`), como antes.
      if (vigente && result) setLista(Array.isArray(result.data) ? result.data : []);
    })();
    return () => {
      vigente = false;
    };
  }, [recargar]);

  const ordenada = [...lista].sort((a, b) => moment(b.fecha).valueOf() - moment(a.fecha).valueOf());
  const hoy = ordenada.find((x) => moment(x.fecha).isSame(moment(), "day"));

  const termino = searchTerm.trim();
  const withSearch = ordenada.filter((x) => termino === "" || formatearFecha(x.fecha).includes(termino));

  useEffect(() => {
    if (pagina > 0 && pagina * FILAS_POR_PAGINA >= withSearch.length) setPagina(0);
  }, [pagina, withSearch.length]);

  const pagActual = withSearch.slice(pagina * FILAS_POR_PAGINA, (pagina + 1) * FILAS_POR_PAGINA);

  const onChangeSearch = (val) => {
    setPagina(0);
    // Solo dígitos y barras: así se escribe una fecha DD/MM/AAAA.
    if (/^[\d/]*$/.test(val)) setSearchTerm(val);
  };

  let contenido;
  if (cargando) {
    contenido = <CargandoTabla filas={8} columnas={2} etiqueta="Cargando el tipo de cambio…" />;
  } else if (error) {
    contenido = (
      <EstadoError
        titulo={error === "conexion" ? "No se pudo conectar con el servidor" : "No se pudo cargar el tipo de cambio"}
        onReintentar={() => setRecargar((actual) => !actual)}
      />
    );
  } else if (withSearch.length === 0) {
    contenido = termino ? (
      <EstadoVacio
        titulo="Sin resultados"
        descripcion={`No hay tipo de cambio para «${termino}». Escriba la fecha como DD/MM/AAAA, o una parte (por ejemplo 09/2026).`}
        accion={
          <Button variant="outlined" onClick={() => onChangeSearch("")}>
            Borrar búsqueda
          </Button>
        }
      />
    ) : (
      <EstadoVacio
        titulo="Todavía no hay tipo de cambio"
        descripcion="Aquí aparece el tipo de cambio oficial de cada día, con el que se convierten los dólares."
      />
    );
  } else {
    contenido = (
      <>
        <TableContainer>
          <Table aria-label="Historial del tipo de cambio">
            <TableHead>
              <TableRow>
                <TableCell sx={encabezadoTabla}>Fecha</TableCell>
                <TableCell sx={{ ...encabezadoTabla, textAlign: "right" }}>Córdobas por US$1</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pagActual.map((row) => (
                <TableRow key={row.id} hover>
                  <TableCell component="th" scope="row" sx={{ fontVariantNumeric: "tabular-nums" }}>
                    {formatearFecha(row.fecha)}
                  </TableCell>
                  <TableCell sx={{ textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                    {formatearMonto(row.dolar)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={withSearch.length}
          page={pagina}
          onPageChange={(e, nueva) => setPagina(nueva)}
          rowsPerPage={FILAS_POR_PAGINA}
          rowsPerPageOptions={[]}
          labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
          getItemAriaLabel={PAGINACION_ARIA}
        />
      </>
    );
  }

  return (
    <Container maxWidth={false} disableGutters>
      <PageHeader
        titulo="Tipo de cambio"
        subtitulo="Córdobas por dólar de cada día, del Banco Central; lo carga el sistema"
        icono={<FontAwesomeIcon icon={faMoneyBillTransfer} />}
      />
      {!cargando && !error && hoy ? (
        <Box sx={{ mb: 2 }}>
          <KpiCard
            titulo={`Hoy, ${formatearFecha(hoy.fecha)}`}
            valor={formatearMonto(hoy.dolar)}
            detalle="Córdobas por US$1"
            icono={<FontAwesomeIcon icon={faMoneyBillTransfer} />}
          />
        </Box>
      ) : null}
      <Paper sx={{ p: { xs: 2, sm: 3 } }}>
        <TextField
          size="small"
          type="search"
          onChange={(e) => onChangeSearch(e.target.value)}
          value={searchTerm}
          placeholder="DD/MM/AAAA"
          inputProps={{ "aria-label": "Buscar por fecha", autoComplete: "off", inputMode: "numeric" }}
          helperText="Escriba la fecha completa o una parte, por ejemplo 09/2026"
          sx={{ width: { xs: "100%", sm: 300 }, mb: 2 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon aria-hidden="true" />
              </InputAdornment>
            ),
            endAdornment: searchTerm ? (
              <InputAdornment position="end">
                <IconButton size="small" aria-label="Borrar búsqueda" onClick={() => onChangeSearch("")}>
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
        />
        {contenido}
      </Paper>
    </Container>
  );
};

export default TasaCambioList;
